import { useState } from "react";
import { datasetLabel } from "../helpers/datasetLabels";
import PersonService from "../components/admin/services/PersonService";

const STAGE_CLOSED = 'closed';
const STAGE_SYNC_CONFIRM = 'sync-confirm';
const STAGE_UNSYNC_WARNING = 'unsync-warning';

// People::PersonMatch::DatasetRecords names datasets by their short code.
const RECORDS_DATASET = {
  RiaContact: 'RIA',
  Contact: 'FO',
  BtContact: 'BT',
  FoundationContact: 'EF',
};

const labelFor = (dataset, datasetContactNames) => {
  const name = datasetContactNames?.[dataset];
  return name ? `${name} (${datasetLabel(dataset)})` : datasetLabel(dataset);
};

export function useLinkedinCrossDatasetConfirm(currentDataset, session) {
  const [stage, setStage] = useState(STAGE_CLOSED);
  const [review, setReview] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewRecords, setReviewRecords] = useState(null);
  const [otherContactLabels, setOtherContactLabels] = useState([]);
  const [currentContactLabel, setCurrentContactLabel] = useState('');
  const [resolve, setResolve] = useState(null);

  // On an import match the url arrived from the person being linked to, so there is nothing to
  // propagate and nothing to ask about. Only while it is still that url: an accepted match stays
  // on the form for the rest of the session, so editing the url afterwards has to be confirmed
  // like any other change - the save still writes it onto the linked person.
  const confirmLinkedinChange = (
    datasets, originalValue, newValue, datasetContactNames,
    {importingMatch = false, importedValue = null, review: reviewSource = null} = {}
  ) => {
    const changed = (originalValue || '') !== (newValue || '');
    const importing = importingMatch && (importedValue || '') === (newValue || '');
    const otherDatasets = (datasets || []).filter((dataset) => dataset !== currentDataset);

    if (!changed || importing || otherDatasets.length === 0) {
      return Promise.resolve(null);
    }

    setCurrentContactLabel(labelFor(currentDataset, datasetContactNames));
    setOtherContactLabels(otherDatasets.map((dataset) => labelFor(dataset, datasetContactNames)));
    setReview(reviewSource?.peopleId ? reviewSource : null);
    setReviewRecords(null);

    return new Promise((res) => {
      setResolve(() => res);
      setStage(STAGE_SYNC_CONFIRM);
    });
  };

  // Any name opens the same comparison: the form next to every other record of the person.
  const openReview = () => {
    setReviewOpen(true);
    if (reviewRecords) return;

    PersonService.datasetRecords({
      people_id: review.peopleId,
      dataset: RECORDS_DATASET[currentDataset],
      record_id: review.recordId,
    }, session)
      .then(({data}) => setReviewRecords(data.records || []))
      .catch(() => setReviewRecords([]));
  };

  const handleSyncConfirm = () => {
    resolve?.('sync');
    setStage(STAGE_CLOSED);
    setResolve(null);
  };

  const handleSyncDecline = () => {
    setStage(STAGE_UNSYNC_WARNING);
  };

  const handleSyncAbort = () => {
    resolve?.('abort');
    setStage(STAGE_CLOSED);
    setResolve(null);
  };

  const handleUnsyncConfirm = () => {
    resolve?.('unsync');
    setStage(STAGE_CLOSED);
    setResolve(null);
  };

  const handleUnsyncDecline = () => {
    setStage(STAGE_SYNC_CONFIRM);
  };

  return {
    linkedinConfirmDialogProps: {
      open: stage === STAGE_SYNC_CONFIRM,
      currentContactLabel,
      otherContactLabels,
      onNameClick: review ? openReview : null,
      onConfirm: handleSyncConfirm,
      onCancel: handleSyncDecline,
      onClose: handleSyncAbort,
    },
    linkedinUnsyncWarningDialogProps: {
      open: stage === STAGE_UNSYNC_WARNING,
      currentDatasetLabel: datasetLabel(currentDataset),
      onConfirm: handleUnsyncConfirm,
      onCancel: handleUnsyncDecline,
    },
    // Closing it only returns to the confirmation, which still holds the save.
    linkedinReviewModalProps: {
      open: reviewOpen && !!reviewRecords && !!review,
      onClose: () => setReviewOpen(false),
      records: reviewRecords || [],
      formCard: review?.formCard,
    },
    confirmLinkedinChange,
  };
}

export default useLinkedinCrossDatasetConfirm;